import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../common/Icon';
import useSpeechToText from '../../hooks/useSpeechToText';

/* A note you can speak or type.

   The textarea is always there and always editable — dictation fills it in, it does
   not replace it.

   ★ Both ways of failing are said out loud. A browser without speech recognition
   (Firefox, and anything on iOS that is not Safari) gets a line explaining that,
   instead of a missing button; a browser that has it but cannot use it — permission
   refused, no microphone, no connection — gets the reason. Silence was the whole
   problem: pressing the button and having nothing at all happen is indistinguishable
   from the feature being broken. */

// The engine's own error codes, which are stable across Chrome and Safari, mapped to
// the four things a rep can actually do something about.
const ERROR_KEYS = {
  'not-allowed': 'salesDictationDenied',
  'service-not-allowed': 'salesDictationDenied',
  'audio-capture': 'salesDictationNoMic',
  network: 'salesDictationOffline',
  busy: 'salesDictationBusy',
};

const VoiceNote = ({ value, onChange, placeholder, rows = 3, label }) => {
  const { t } = useTranslation();
  const { supported, listening, transcript, error, start, stop, setTranscript } = useSpeechToText();

  // What we last handed up. Comparing against this rather than against `value`
  // means the LAST words — the ones finalised as the mic closes, after `listening`
  // has already gone false — still reach the caller, while the rep's own typing is
  // never overwritten.
  const pushed = useRef('');

  useEffect(() => {
    if (transcript === pushed.current) return;
    pushed.current = transcript;
    onChange(transcript);
    // Following `value` here would fight the rep's own typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transcript]);

  const toggle = () => {
    if (listening) { stop(); return; }
    pushed.current = value || '';
    setTranscript(value || '');
    start(value || '');
  };

  return (
    <div className="sales-voice">
      {label && <label className="sales-label">{label}</label>}
      <textarea
        className="sales-input sales-voice__box"
        rows={rows}
        value={value || ''}
        placeholder={placeholder || t('salesNotePlaceholder')}
        onChange={(e) => onChange(e.target.value)}
      />
      {supported ? (
        <button
          type="button"
          className={`sales-voice__mic ${listening ? 'is-listening' : ''}`}
          onClick={toggle}
          aria-label={listening ? t('salesStopDictation') : t('salesDictate')}
        >
          <Icon name={listening ? 'stop' : 'microphone'} />
          <span>{listening ? t('salesStopDictation') : t('salesDictate')}</span>
        </button>
      ) : (
        <p className="sales-hint">{t('salesDictationUnsupported')}</p>
      )}
      {error && (
        <p className="sales-error">{t(ERROR_KEYS[error] || 'salesDictationFailed')}</p>
      )}
    </div>
  );
};

export default VoiceNote;
